/* ═══════════════════════════════════════════════════════════════════════
   FizzyIQ — Full FAQ page (v5 — nav+search left, accordions right)

   Three responsibilities:
     1. Search filter — input filters accordion items by question
        and answer text (case-insensitive). Empty-category sections hide.
        Nav items for empty categories also dim/hide.
     2. Nav click → smooth-scroll to the matching category section
        anchor. URL hash syncs via replaceState.
     3. Active-nav highlight on scroll — IntersectionObserver on
        each section determines which is currently in view; matching
        nav link gets `is-active`.
   Sam: 2026-06-01.
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  if (window.__fizzyPageFaqV5Booted) return;
  window.__fizzyPageFaqV5Booted = true;

  function boot() {
    var root = document.querySelector('.fp-faq');
    if (!root) return;

    var input    = root.querySelector('.fp-faq-search-input');
    var clearBtn = root.querySelector('.fp-faq-search-clear');
    var countEl  = root.querySelector('.fp-faq-search-count');
    var emptyEl  = root.querySelector('.fp-faq-empty');
    var emptyTerm = root.querySelector('.fp-faq-empty-term');

    var items    = Array.prototype.slice.call(root.querySelectorAll('.fp-faq-item'));
    var sections = Array.prototype.slice.call(root.querySelectorAll('.fp-faq-cat'));
    var navItems = Array.prototype.slice.call(root.querySelectorAll('.fp-faq-nav-item'));
    var navLinks = Array.prototype.slice.call(root.querySelectorAll('.fp-faq-nav-link'));

    /* Keep the muted hero loop running when visible, including tab restores. */
    (function heroVideo() {
      var video = root.querySelector('.fp-faq-hero-video');
      if (!video) return;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      var rect = video.getBoundingClientRect();
      var inView = rect.bottom > 0 && rect.top < window.innerHeight;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      function syncPlayback() {
        var shouldPlay = !reduced.matches && !document.hidden && inView;
        video.autoplay = shouldPlay;
        if (!shouldPlay) {
          video.pause();
          return;
        }
        var playback = video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(function () {});
      }
      video.addEventListener('canplay', syncPlayback);
      document.addEventListener('visibilitychange', syncPlayback);
      window.addEventListener('pageshow', syncPlayback);
      if (reduced.addEventListener) reduced.addEventListener('change', syncPlayback);
      else if (reduced.addListener) reduced.addListener(syncPlayback);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          syncPlayback();
        }, { threshold: 0 }).observe(video);
      }
      syncPlayback();
    })();

    if (!items.length || !sections.length) return;

    /* ── Nav click → smooth-scroll to section ─────────────────────── */
    var linkByCat = {};
    navLinks.forEach(function (a) { linkByCat[a.dataset.cat] = a; });

    function setActiveNav(cat) {
      if (!cat || !linkByCat[cat]) return;
      navLinks.forEach(function (a) {
        a.classList.toggle('is-active', a === linkByCat[cat]);
      });
    }

    navLinks.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var cat = a.dataset.cat;
        var target = document.getElementById('cat-' + cat);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '#cat-' + cat);
        }
        setActiveNav(cat);
      });
    });

    /* ── Scroll → active nav highlight ────────────────────────────── */
    /* IntersectionObserver with a rootMargin that creates an "active
       band" near the top of the viewport. The topmost section in
       that band is the active one. */
    var inView = new Set();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) inView.add(entry.target);
        else inView.delete(entry.target);
      });
      if (!inView.size) return;
      var topmost = null;
      var topY = Infinity;
      inView.forEach(function (el) {
        var y = el.getBoundingClientRect().top;
        if (y < topY) { topY = y; topmost = el; }
      });
      if (topmost) setActiveNav(topmost.dataset.cat);
    }, {
      rootMargin: '-120px 0px -55% 0px',
      threshold: 0
    });
    sections.forEach(function (s) { io.observe(s); });

    /* ── Search filter ────────────────────────────────────────────── */
    function applyFilter() {
      if (!input) return;
      var term = input.value.trim().toLowerCase();
      var visibleCount = 0;

      items.forEach(function (it) {
        var match = term === '' || (it.dataset.search || '').indexOf(term) !== -1;
        it.classList.toggle('is-hidden', !match);
        if (match) visibleCount++;
        /* Collapse hidden accordions so they don't re-expand
           awkwardly when the filter clears. */
        if (!match && it.open) {
          /* Search filtering closes non-matching answers — instant here on
             purpose: this is a bulk filter, not a user dismissing one row,
             and animating a dozen collapses at once reads as lag. */
          it.open = false;
          it.classList.remove('is-open');
        }
      });

      /* Category sections + nav items hide when all their items are
         filtered out. */
      sections.forEach(function (sec) {
        var anyVisible = sec.querySelectorAll('.fp-faq-item:not(.is-hidden)').length > 0;
        sec.classList.toggle('is-hidden', !anyVisible);
      });
      navItems.forEach(function (li) {
        var cat = li.dataset.catNav;
        var matchingSec = sections.find(function (s) { return s.dataset.cat === cat; });
        var anyVisible = matchingSec && !matchingSec.classList.contains('is-hidden');
        li.classList.toggle('is-hidden', !anyVisible);
      });

      if (countEl) {
        if (term === '') countEl.textContent = '';
        else countEl.textContent = visibleCount + ' result' + (visibleCount === 1 ? '' : 's');
      }
      if (emptyEl) {
        var showEmpty = term !== '' && visibleCount === 0;
        emptyEl.hidden = !showEmpty;
        if (emptyTerm) emptyTerm.textContent = input.value.trim();
      }
      if (clearBtn) clearBtn.hidden = term === '';
    }

    if (input) input.addEventListener('input', applyFilter);
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (!input) return;
        input.value = '';
        input.focus();
        applyFilter();
      });
    }

    /* ── Initial state — respect URL hash if it matches a category. */
    var initial = (window.location.hash || '').replace('#cat-', '');
    if (initial && linkByCat[initial]) {
      setActiveNav(initial);
      requestAnimationFrame(function () {
        var target = document.getElementById('cat-' + initial);
        if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
